/*
 * Copyright (c) 2017 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import {
  ComponentFactoryResolver, ComponentRef, Directive, ElementRef, Input, Renderer2, Type, ViewContainerRef, Injector, ApplicationRef
} from '@angular/core';
import { equals } from '@typescript-kit/core';
import { ComponentModel } from '@typescript-kit/view';
import { BaseDirective } from './base.directive';
import { ComponentProvider } from '../provider/component.provider';

@Directive({
  selector: '[kitComponent]',
})
export class ComponentDirective {

  private _model: ComponentModel;
  private componentRef: ComponentRef<BaseDirective<ComponentModel>>;
  private _componentClass: string[];
  private _componentAttributes: { [key: string]: string };

  constructor(
    protected injector: Injector,
    protected applicationRef: ApplicationRef,
    private renderer: Renderer2,
    private viewContainerRef: ViewContainerRef,
    private componentFactoryResolver: ComponentFactoryResolver
  ) { }

  @Input('kitComponent')
  set model(model: ComponentModel) {
    if (model === this._model) {
      return;
    }
    this.viewContainerRef.clear();
    this.componentRef = null;
    if (this._model) {
      // TODO -> destroy ,and detach view;
    }
    this._model = model;
    if (model) {
      const componentType: Type<any> = ComponentProvider.resolveComponentType(model.type);
      if (!componentType) {
        // TODO
        // componentType = UndefinedComponent;
        console.log(`Component type not found.`, model.toJSON());
        return;
      }
      const componentFactory = this.componentFactoryResolver.resolveComponentFactory(componentType);
      const hostNode = this.viewContainerRef.element.nativeElement as Node;
      if (hostNode.nodeType === Node.ELEMENT_NODE) {
        this.componentRef = componentFactory.create(this.injector, [], hostNode);
        // Attach to the view so that the change detector knows to run
        this.applicationRef.attachView(this.componentRef.hostView);
      } else {
        this.componentRef = this.viewContainerRef.createComponent(componentFactory);
      }
      this.componentRef.instance.model = model;
      const element: ElementRef = this.componentRef.location;
      if (this.componentClass) {
        for (const value of this.componentClass) {
          this.renderer.addClass(element.nativeElement, value);
        }
      }
      if (this.componentAttributes) {
        Object.keys(this.componentAttributes).forEach((attributeKey: string) => {
          this.renderer.setAttribute(element.nativeElement, attributeKey, this.componentAttributes[attributeKey]);
        });
      }
    }
  }

  get model(): ComponentModel {
    return this._model;
  }

  get componentClass(): string | string[] {
    return this._componentClass;
  }

  @Input('kitComponentClass')
  set componentClass(value: string | string[]) {
    if (!Array.isArray(value)) {
      value = value ? value.split(' ') : null;
    }
    const originalValue = this._componentClass;
    if (equals(originalValue, value)) {
      return;
    }
    this._componentClass = value;
    if (this.componentRef) {
      const element: ElementRef = this.componentRef.location;
      if (originalValue) {
        for (const classString of originalValue) {
          this.renderer.removeClass(element.nativeElement, classString);
        }
      }
      if (value) {
        for (const classString of value) {
          this.renderer.addClass(element.nativeElement, classString);
        }
      }
    }
  }

  @Input('kitComponentAttributes')
  set componentAttributes(value: { [key: string]: string }) {
    if (equals(this._componentAttributes, value)) {
      return;
    }
    const previousAttributes = this._componentAttributes;
    this._componentAttributes = value;
    if (this.componentRef) {
      const element: ElementRef = this.componentRef.location;
      if (previousAttributes) {
        Object.keys(previousAttributes).forEach((attributeKey: string) => {
          this.renderer.removeAttribute(element.nativeElement, attributeKey);
        });
      }
      if (value) {
        Object.keys(value).forEach((attributeKey: string) => {
          this.renderer.setAttribute(element.nativeElement, attributeKey, value[attributeKey]);
        });
      }
    }
  }

  get componentAttributes(): { [key: string]: string } {
    return this._componentAttributes;
  }
}
